import { useMemo } from 'react';

const W = 300;
const H = 200;
const IMPACTS = [[238, 46], [52, 150], [250, 168], [40, 36]];

function rng(seed) {
    let a = seed * 2654435761;
    return () => {
        a |= 0; a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

// Spider-web fracture: jagged radial rays + partial concentric rings + a few glinting shards.
function buildFracture(variant) {
    const r = rng(variant + 7);
    const [ox, oy] = IMPACTS[variant % IMPACTS.length];
    const RADII = [14, 34, 66, 110];
    const rayCount = 8 + Math.floor(r() * 3);
    const rays = Array.from({ length: rayCount }, (_, i) => {
        const angle = (i / rayCount) * Math.PI * 2 + (r() - 0.5) * 0.5;
        const reach = 0.55 + r() * 0.6;
        const pts = [[ox, oy]];
        RADII.forEach((rad) => {
            const a = angle + (r() - 0.5) * 0.18;
            const d = rad * (0.85 + r() * 0.3);
            pts.push([ox + Math.cos(a) * d, oy + Math.sin(a) * d]);
        });
        const end = 260 * reach;
        pts.push([ox + Math.cos(angle) * end, oy + Math.sin(angle) * end]);
        return pts;
    });
    const rings = [];
    const shards = [];
    rays.forEach((ray, i) => {
        const next = rays[(i + 1) % rays.length];
        [1, 2, 3].forEach((k) => {
            if (r() < (k === 3 ? 0.45 : 0.75)) {
                const [x1, y1] = ray[k];
                const [x2, y2] = next[k];
                const mx = (x1 + x2) / 2 + (ox - (x1 + x2) / 2) * 0.12;
                const my = (y1 + y2) / 2 + (oy - (y1 + y2) / 2) * 0.12;
                rings.push({ d: `M${x1} ${y1} Q${mx} ${my} ${x2} ${y2}`, o: 0.5 + r() * 0.5 });
            }
        });
        if (r() < 0.4) {
            const k = 1 + Math.floor(r() * 2);
            shards.push({
                d: `M${ray[k][0]} ${ray[k][1]} L${ray[k + 1][0]} ${ray[k + 1][1]} L${next[k + 1][0]} ${next[k + 1][1]} L${next[k][0]} ${next[k][1]} Z`,
                o: 0.1 + r() * 0.18,
            });
        }
    });
    const toPath = (pts) => pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
    return { ox, oy, rays: rays.map(toPath), rings, shards };
}

export function GlassCracks({ variant = 0 }) {
    const f = useMemo(() => buildFracture(variant), [variant]);
    const gid = `glint-${variant}`;
    return (
        <svg
            aria-hidden="true"
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="xMidYMid slice"
            className="absolute inset-0 w-full h-full pointer-events-none"
            data-testid="glass-cracks"
        >
            <defs>
                <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor="#fff" stopOpacity="0.95" />
                    <stop offset="1" stopColor="#fff" stopOpacity="0.1" />
                </linearGradient>
            </defs>
            {f.shards.map((s, i) => (
                <path key={`s${i}`} d={s.d} fill={`url(#${gid})`} opacity={s.o} />
            ))}
            <g fill="none" strokeLinecap="round" strokeLinejoin="round">
                {f.rays.map((d, i) => (
                    <g key={`r${i}`}>
                        <path d={d} stroke="#fff" strokeOpacity="0.85" strokeWidth="1.8" transform="translate(0.6 0.6)" />
                        <path d={d} stroke="#1e293b" strokeOpacity="0.32" strokeWidth="0.7" />
                    </g>
                ))}
                {f.rings.map((ring, i) => (
                    <g key={`c${i}`} opacity={ring.o}>
                        <path d={ring.d} stroke="#fff" strokeOpacity="0.8" strokeWidth="1.4" transform="translate(0.5 0.5)" />
                        <path d={ring.d} stroke="#1e293b" strokeOpacity="0.26" strokeWidth="0.55" />
                    </g>
                ))}
            </g>
            <circle cx={f.ox} cy={f.oy} r="5" fill="#fff" opacity="0.55" />
            <circle cx={f.ox} cy={f.oy} r="1.6" fill="#1e293b" opacity="0.35" />
        </svg>
    );
}
